import { useEffect, useMemo, useState, useCallback } from "react";
import { http, fmtEUR, warnZeroStock, stockBadge } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Truck,
  Plus,
  Phone,
  MapPin,
  StickyNote,
  User as UserIcon,
  X,
  Trash2,
  Pencil,
  PlayCircle,
  Check,
  XCircle,
  Banknote,
  CreditCard,
  Users as UsersIcon,
  Search,
  UserPlus,
  LayoutGrid,
  Table as TableIcon,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Eye,
  Clock,
  Receipt,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import CustomerPickerDialog from "@/components/CustomerPickerDialog";
import CustomerTopItemsDialog from "@/components/CustomerTopItemsDialog";
import DeliveryNewOrder from "@/components/DeliveryNewOrder";
import DeliveryActionsMenu from "@/components/DeliveryActionsMenu";
import DeliveryOrderPreviewDialog from "@/components/DeliveryOrderPreviewDialog";
import DeliveryDriverHub from "@/components/DeliveryDriverHub";
import DeliveryHoldsDialog from "@/components/DeliveryHoldsDialog";
import { loadHolds, removeHold } from "@/lib/deliveryHolds";
import { printProforma } from "@/lib/proforma";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";

const STATUS_META = {
  NEW: { label: "Νέα", color: "bg-amber-100 text-amber-800 border-amber-300" },
  DISPATCHED: { label: "Σε διανομή", color: "bg-blue-100 text-blue-800 border-blue-300" },
  DELIVERED: { label: "Παραδόθηκε", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  CANCELLED: { label: "Ακυρώθηκε", color: "bg-slate-200 text-slate-600 border-slate-300" },
};

function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return "τώρα";
  if (mins < 60) return `${mins}'`;
  const hr = Math.floor(mins / 60);
  if (hr < 24) return `${hr}ω ${mins % 60}'`;
  return new Date(iso).toLocaleString("el-GR");
}

export default function Delivery() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "supervisor";

  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("ACTIVE"); // ACTIVE | DELIVERED | CANCELLED
  const [formOpen, setFormOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  // Feb 2026 — new full-screen «Νέα Παραγγελία» editor that mirrors the
  // Restaurant table-order layout. Used for *new* deliveries; editing
  // still goes through the legacy compact OrderFormDialog so partially
  // dispatched orders remain editable.
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [driversOpen, setDriversOpen] = useState(false);
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [dispatchTarget, setDispatchTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null);

  // «Επανάληψη» — template order used to pre-fill DeliveryNewOrder
  // with the items + customer of a DELIVERED / CANCELLED order while
  // posting it as a brand-new entry.
  const [repeatTemplate, setRepeatTemplate] = useState(null);

  // Local-storage backed «Παραγγελίες σε Αναμονή». Synced via
  // refreshHolds() after every add/resume/delete + on Delivery
  // mount so the badge count stays current across tab switches.
  const [holds, setHolds] = useState(() => loadHolds());
  const [holdsOpen, setHoldsOpen] = useState(false);
  const refreshHolds = useCallback(() => setHolds(loadHolds()), []);

  // Read-only preview dialog opened from a «Προεπισκόπηση» button on
  // every order card / row. Clicking outside (Radix overlay) closes it.
  const [previewOrder, setPreviewOrder] = useState(null);
  const onPreview = (o) => setPreviewOrder(o);

  // Auto-sync `payTarget` whenever the canonical orders list updates —
  // keeps the «Αποστολή» dialog header in step after a driver
  // assignment (or any other mutation) without forcing the operator
  // to re-open it.
  useEffect(() => {
    if (!payTarget) return;
    const fresh = orders.find((o) => o.id === payTarget.id);
    if (fresh && fresh !== payTarget) setPayTarget(fresh);
  }, [orders, payTarget]);

  // View-mode toggle — «Κάρτες» (default) ή «Πίνακας» (sortable table).
  // Persisted in localStorage so each operator keeps their preferred
  // layout between sessions.
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem("delivery_view_mode") === "table" ? "table" : "cards";
    } catch {
      return "cards";
    }
  });
  useEffect(() => {
    try { localStorage.setItem("delivery_view_mode", viewMode); } catch { /* noop */ }
  }, [viewMode]);

  // Sortable-column state used only when viewMode === "table".
  // dir is "asc" | "desc". A second click on the same header toggles
  // direction; clicking another header resets dir to "asc".
  const [sort, setSort] = useState({ key: "order_no", dir: "desc" });
  const toggleSort = (key) => {
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const load = useCallback(async () => {
    try {
      const [o, d, s] = await Promise.all([
        http.get("/delivery/orders?limit=300"),
        http.get("/drivers"),
        http.get("/settings"),
      ]);
      setOrders(o.data || []);
      setDrivers(d.data || []);
      setSettings(s.data || null);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    const t0 = setTimeout(load, 0);
    const tk = setInterval(load, 8000);
    return () => {
      clearTimeout(t0);
      clearInterval(tk);
    };
  }, [load]);

  // Poll the delivery-code counter so the toolbar can surface an
  // early-warning banner once the 4-digit code approaches its ceiling.
  // Polling every 30 s is plenty — the counter only moves on new orders.
  const [codeCounter, setCodeCounter] = useState({ current: 0, next: 1, warning_level: "none" });
  useEffect(() => {
    let mounted = true;
    const fetchCounter = () => {
      http.get("/delivery/code-counter")
        .then((r) => { if (mounted) setCodeCounter(r.data); })
        .catch(() => {});
    };
    fetchCounter();
    const id = setInterval(fetchCounter, 30000);
    return () => { mounted = false; clearInterval(id); };
  }, [orders.length]); // re-fetch right after a new order is created

  const filtered = useMemo(() => {
    let rows;
    if (tab === "ACTIVE") rows = orders.filter((o) => o.status === "NEW" || o.status === "DISPATCHED");
    else if (tab === "DELIVERED") rows = orders.filter((o) => o.status === "DELIVERED");
    else if (tab === "CANCELLED") rows = orders.filter((o) => o.status === "CANCELLED");
    else rows = orders;

    // Sorting is only meaningful in table view, but keep the cards view
    // deterministic too by always honouring the active sort key.
    const { key, dir } = sort;
    const factor = dir === "asc" ? 1 : -1;
    const get = (o) => {
      switch (key) {
        case "order_no": return Number(o.order_no || 0);
        case "status": return STATUS_META[o.status]?.label || "";
        case "created_at": return new Date(o.created_at || 0).getTime();
        case "customer_name": return (o.customer_name || "").toLowerCase();
        case "phone": return o.phone || "";
        case "address": return (o.address || "").toLowerCase();
        case "items": return (o.items || []).reduce((s, it) => s + (Number(it.qty) || 1), 0);
        case "total": return Number(o.total || 0);
        case "driver_name": return (o.driver_name || "").toLowerCase();
        default: return 0;
      }
    };
    return [...rows].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (av < bv) return -1 * factor;
      if (av > bv) return 1 * factor;
      return 0;
    });
  }, [orders, tab, sort]);

  const counts = useMemo(() => {
    const c = { NEW: 0, DISPATCHED: 0, DELIVERED: 0, CANCELLED: 0 };
    orders.forEach((o) => {
      c[o.status] = (c[o.status] || 0) + 1;
    });
    return c;
  }, [orders]);

  const onNew = () => {
    // Use the new full-screen editor (Restaurant-style layout) rather
    // than the legacy compact form.
    setEditingOrder(null);
    setNewOrderOpen(true);
  };

  const onEdit = (o) => {
    // Open the full-screen Restaurant-style editor instead of the legacy
    // compact dialog. Operators expect the same touch-friendly layout
    // whether they create or correct an order.
    setEditingOrder(o);
    setNewOrderOpen(true);
  };

  const onCancel = async (o) => {
    if (!(await confirmDialog(`Ακύρωση παραγγελίας #${o.order_no};`))) return;
    try {
      await http.patch(`/delivery/orders/${o.id}/cancel`);
      toast.success("Ακυρώθηκε");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    }
  };

  const onDispatch = (o) => {
    setDispatchTarget(o);
    setDispatchOpen(true);
  };

  const onPay = (o) => {
    setPayTarget(o);
  };

  // -----------------------------------------------------------------
  // «Ενέργειες» menu actions for DELIVERED / CANCELLED orders

  // Re-print the order proforma using the unified template (see
  // `lib/proforma.js`). The same builder powers Retail and Restaurant
  // proforma prints — empty fields are auto-skipped row-by-row.
  const onReprint = async (o) => {
    // Resolve the full address from the customer card when possible.
    let addressRows = [];
    if (o.customer_id) {
      try {
        const r = await http.get(`/customers/${o.customer_id}`);
        const cust = r.data;
        const adr =
          (cust.addresses || []).find(
            (a) => a && o.address && (
              o.address.includes(a.area || "") ||
              o.address.includes(a.street || "")
            )
          ) ||
          (cust.addresses || []).find((a) => a.is_default) ||
          (cust.addresses || [])[0] || null;
        if (adr) {
          const street = [adr.street, adr.street_no].filter(Boolean).join(" ");
          if (street) addressRows.push({ label: "Διεύθυνση", value: street });
          if (adr.area) addressRows.push({ label: "Περιοχή", value: adr.area });
          if (adr.floor) addressRows.push({ label: "Όροφος", value: adr.floor });
          if (adr.doorbell) addressRows.push({ label: "Κουδούνι", value: adr.doorbell });
          if (adr.instructions) addressRows.push({ label: "Οδηγίες", value: adr.instructions });
        }
      } catch {
        /* noop */
      }
    }
    if (addressRows.length === 0 && o.address) {
      addressRows.push({ label: "Διεύθυνση", value: o.address });
    }
    // Iter87: try Companion thermal printer via /print/proforma first
    // (works in packaged Kiosk). Fall back to browser preview only if
    // no printer is configured.
    //
    // Feb 2026 bug fix: The printed CODE128 barcode MUST use the
    // delivery-specific `code` (4-digit, matches the scanner's
    // `/delivery/lookup-by-code/{code}` lookup) — NOT `global_order_no`
    // which is the cross-channel counter and would fail to match any
    // delivery record on scan. We send `o.code` as `order_no` so both
    // the printed header AND the 7777XXXX barcode use the same
    // delivery identifier that the driver-hub scanner resolves.
    try {
      const stationId = localStorage.getItem("selected_station_id") || null;
      const r = await http.post("/print/proforma", {
        items: o.items || [],
        total: o.total || 0,
        title: "ΔΙΑΝΟΜΗ",
        template_kind: "delivery",
        customer_name: o.customer_name || null,
        customer_phone: o.phone || null,
        customer_address: o.address || null,
        delivery_notes: o.notes || null,
        delivery_time: o.delivery_time || null,
        driver_name: o.driver_name || null,
        order_no: o.code || o.order_no || o.global_order_no,
        station_id: stationId,
      });
      if (r?.data?.ok) {
        toast.success(`Επανεκτύπωση #${o.order_no} (${r.data.printer || "εκτυπωτής"})`);
        return;
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (typeof detail === "string" && detail.includes("εκτυπωτής")) {
        toast.info("Χωρίς εκτυπωτή — χρήση browser preview");
      } else {
        toast.error(detail || "Αποτυχία εκτύπωσης");
        return;
      }
    }
    const res = await printProforma({
      settings,
      code: o.code,
      globalOrderNo: o.global_order_no,
      createdAt: o.created_at,
      title: "ΔΙΑΝΟΜΗ",
      customerName: o.customer_name,
      phone: o.phone,
      addressRows,
      notes: o.notes,
      items: o.items || [],
      total: o.total || 0,
      driverName: o.driver_name,
    });
    if (!res.ok && res.reason === "popup_blocked") {
      toast.error("Ο browser απέρριψε το popup εκτύπωσης. Επιτρέψτε popups για αυτή τη σελίδα.");
      return;
    }
    toast.success(`Επανεκτύπωση παραγγελίας #${o.order_no}`);
  };

  // «Απόδειξη» — same as the active-order pay flow, just routed from
  // the actions menu for closed orders so the cashier can issue the
  // fiscal receipt after the fact.
  const onReceipt = (o) => setPayTarget(o);

  // «Επανάληψη» — open the new-order editor with the items + customer
  // of the closed order pre-loaded. The submit will POST a fresh order.
  const onRepeat = (o) => {
    setRepeatTemplate(o);
    setNewOrderOpen(true);
  };

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <div className="px-6 py-4 border-b border-slate-200 bg-white flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Truck size={22} /> Διανομές
          </h2>
          <div className="text-xs text-slate-500">
            Λήψη παραγγελιών, ανάθεση οδηγού, παράδοση με έκδοση απόδειξης.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* View-mode toggle */}
          <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
            <button
              type="button"
              onClick={() => setViewMode("cards")}
              className={`px-3 h-10 flex items-center gap-1.5 text-xs font-bold transition-colors ${
                viewMode === "cards"
                  ? "bg-slate-800 text-white"
                  : "bg-white text-slate-700 hover:bg-slate-50"
              }`}
              data-testid="delivery-view-cards"
              title="Προβολή Καρτών"
            >
              <LayoutGrid size={14} /> Κάρτες
            </button>
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`px-3 h-10 flex items-center gap-1.5 text-xs font-bold transition-colors border-l border-slate-300 ${
                viewMode === "table"
                  ? "bg-slate-800 text-white"
                  : "bg-white text-slate-700 hover:bg-slate-50"
              }`}
              data-testid="delivery-view-table"
              title="Προβολή Πίνακα"
            >
              <TableIcon size={14} /> Πίνακας
            </button>
          </div>

          <Button
            variant="outline"
            onClick={() => setDriversOpen(true)}
            data-testid="delivery-drivers-btn"
            className="h-10"
          >
            <UsersIcon size={16} className="mr-1.5" /> Οδηγοί ({drivers.filter((d) => d.on_shift).length}/{drivers.length})
          </Button>
          {holds.length > 0 && (
            <Button
              variant="outline"
              onClick={() => setHoldsOpen(true)}
              data-testid="delivery-holds-btn"
              className="h-10 bg-amber-50 hover:bg-amber-100 border-amber-300 text-amber-800 font-bold"
              title="Παραγγελίες σε αναμονή"
            >
              <Clock size={16} className="mr-1.5" /> Αναμονή ({holds.length})
            </Button>
          )}
          <Button
            onClick={onNew}
            className="bg-emerald-600 hover:bg-emerald-700 h-10 text-base"
            data-testid="delivery-new-btn"
          >
            <Plus size={18} className="mr-1.5" /> Νέα Παραγγελία
          </Button>
        </div>
      </div>

      {/* Code-counter warning banner — surfaces when the 4-digit
          delivery code approaches its ceiling. Two states:
          warning (9000–9998) and critical (≥9999). */}
      {codeCounter.warning_level !== "none" && (
        <div
          className={`mx-6 mt-3 rounded-md border-2 px-4 py-2.5 flex items-center justify-between gap-3 ${
            codeCounter.warning_level === "critical"
              ? "bg-red-50 border-red-300 text-red-800"
              : "bg-amber-50 border-amber-300 text-amber-900"
          }`}
          data-testid={`delivery-code-warning-${codeCounter.warning_level}`}
        >
          <div className="flex items-center gap-2 text-sm">
            <span className="text-lg">{codeCounter.warning_level === "critical" ? "⛔" : "⚠️"}</span>
            <span>
              {codeCounter.warning_level === "critical" ? (
                <>
                  Ο κωδικός παραγγελιών έφτασε το όριο των 4 ψηφίων (
                  <span className="font-mono font-bold">{String(codeCounter.current).padStart(4, "0")}</span>).
                  Μηδενίστε από το μενού των Αναφορών.
                </>
              ) : (
                <>
                  Ο κωδικός παραγγελιών πλησιάζει στο όριο (
                  <span className="font-mono font-bold">{String(codeCounter.current).padStart(4, "0")}</span> / 9999).
                  Σύντομα θα χρειαστεί μηδενισμός από Αναφορές.
                </>
              )}
            </span>
          </div>
          <a
            href="/reports"
            className="shrink-0 px-3 h-9 inline-flex items-center rounded font-bold text-xs bg-white border border-current hover:bg-slate-50"
            data-testid="delivery-code-warning-link"
          >
            Άνοιγμα Αναφορών →
          </a>
        </div>
      )}

      <div className="px-6 pt-3 flex gap-2">
        <TabBtn active={tab === "ACTIVE"} onClick={() => setTab("ACTIVE")} testid="tab-active">
          Ενεργές
          <span className="ml-2 inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded-full text-[11px] font-bold bg-amber-600 text-white">
            {counts.NEW + counts.DISPATCHED}
          </span>
        </TabBtn>
        <TabBtn active={tab === "DELIVERED"} onClick={() => setTab("DELIVERED")} testid="tab-delivered">
          Παραδομένες
          <span className="ml-2 inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded-full text-[11px] font-bold bg-emerald-600 text-white">
            {counts.DELIVERED}
          </span>
        </TabBtn>
        <TabBtn active={tab === "CANCELLED"} onClick={() => setTab("CANCELLED")} testid="tab-cancelled">
          Ακυρωμένες
          <span className="ml-2 inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded-full text-[11px] font-bold bg-slate-500 text-white">
            {counts.CANCELLED}
          </span>
        </TabBtn>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin px-6 pb-6 pt-3">
        {filtered.length === 0 ? (
          <div className="h-full grid place-items-center text-slate-400">
            <div className="text-center">
              <Truck size={48} className="mx-auto mb-3 opacity-40" />
              <div className="text-sm">Καμία παραγγελία</div>
            </div>
          </div>
        ) : viewMode === "cards" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
            {filtered.map((o) => (
              <OrderCard
                key={o.id}
                order={o}
                onEdit={() => onEdit(o)}
                onCancel={() => onCancel(o)}
                onDispatch={() => onDispatch(o)}
                onPay={() => onPay(o)}
                onPreview={onPreview}
                onReprint={onReprint}
                onReceipt={onReceipt}
                onRepeat={onRepeat}
              />
            ))}
          </div>
        ) : (
          <OrderTable
            orders={filtered}
            sort={sort}
            onToggleSort={toggleSort}
            onEdit={onEdit}
            onCancel={onCancel}
            onDispatch={onDispatch}
            onPay={onPay}
            onPreview={onPreview}
            onReprint={onReprint}
            onReceipt={onReceipt}
            onRepeat={onRepeat}
          />
        )}
      </div>

      {formOpen && (
        <OrderFormDialog
          order={editingOrder}
          drivers={drivers}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            setFormOpen(false);
            load();
          }}
        />
      )}

      <DeliveryOrderPreviewDialog
        open={!!previewOrder}
        onOpenChange={(o) => { if (!o) setPreviewOrder(null); }}
        order={previewOrder}
      />

      {newOrderOpen && (
        <DeliveryNewOrder
          open={newOrderOpen}
          drivers={drivers}
          order={editingOrder}
          template={repeatTemplate}
          activeOrders={orders.filter((o) => o.status === "NEW" || o.status === "DISPATCHED")}
          onSwitchToExisting={(po) => {
            // Close the new-order editor and immediately reopen it in
            // edit-mode against the picked pending order.
            setRepeatTemplate(null);
            setEditingOrder(po);
          }}
          onClose={() => { setNewOrderOpen(false); setEditingOrder(null); setRepeatTemplate(null); }}
          onSubmitted={() => {
            setNewOrderOpen(false);
            setEditingOrder(null);
            setRepeatTemplate(null);
            load();
          }}
          onHeld={() => { refreshHolds(); }}
        />
      )}

      {holdsOpen && (
        <DeliveryHoldsDialog
          open={holdsOpen}
          holds={holds}
          onClose={() => setHoldsOpen(false)}
          onResume={(h) => {
            // Pop the snapshot from storage and feed it as a template
            // to the editor — reuse the existing prefill plumbing.
            removeHold(h.id);
            refreshHolds();
            setHoldsOpen(false);
            setEditingOrder(null);
            setRepeatTemplate({
              _resumed_hold: true,
              items: h.items || [],
              customer_id: h.customer?.id,
              customer_name: h.customer?.name,
              phone: h.customer?.phones?.[0],
              address: h.address,
              notes: h.notes || "",
              driver_id: h.driverId || "",
            });
            setNewOrderOpen(true);
          }}
          onDelete={(h) => {
            removeHold(h.id);
            refreshHolds();
          }}
        />
      )}

      {driversOpen && (
        <DeliveryDriverHub
          isAdmin={isAdmin}
          onClose={() => setDriversOpen(false)}
          onChanged={load}
        />
      )}

      {dispatchOpen && dispatchTarget && (
        <DispatchDialog
          order={dispatchTarget}
          drivers={drivers}
          onClose={() => setDispatchOpen(false)}
          onSaved={() => {
            setDispatchOpen(false);
            setDispatchTarget(null);
            load();
          }}
        />
      )}

      {payTarget && (
        <DeliveryPayDialog
          order={payTarget}
          onClose={() => setPayTarget(null)}
          onPaid={() => {
            setPayTarget(null);
            load();
          }}
          onPrintProforma={(o) => { onReprint(o); }}
          onAssignDriver={(o) => {
            // Keep the pay dialog mounted — the dispatch dialog opens
            // on top; after it closes we auto-refresh `payTarget` from
            // the reloaded `orders` list (see the useEffect below) so
            // the «Οδηγός» line in the header reflects the new
            // assignment without the operator re-opening it.
            onDispatch(o);
          }}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 h-10 rounded-t-md border-b-2 font-bold text-sm flex items-center gap-1.5 transition-colors ${
        active
          ? "border-blue-600 text-blue-700 bg-white"
          : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100"
      }`}
    >
      {children}
    </button>
  );
}

function OrderCard({ order, onEdit, onCancel, onDispatch, onPay, onPreview, onReprint, onReceipt, onRepeat }) {
  const meta = STATUS_META[order.status] || STATUS_META.NEW;
  const itemsTotal = (order.items || []).reduce((s, it) => s + (Number(it.qty) || 1), 0);
  const isActive = order.status === "NEW" || order.status === "DISPATCHED";
  const isClosed = order.status === "DELIVERED" || order.status === "CANCELLED";
  // Feb 2026 spec: παραγγελία με απόδειξη εκδομένη αλλά ΧΩΡΙΣ ανατεθειμένο
  // οδηγό παραμένει στις Ενεργές — τη χρωματίζουμε με πορτοκαλί border
  // και δείκτη "Απόδειξη έτοιμη" ώστε ο ταμίας να δει άμεσα ποιες
  // περιμένουν driver assignment.
  const awaitingDriver = isActive && !!order.receipt_id && !order.driver_id;
  return (
    <Card
      className={`p-4 hover:shadow-md transition-shadow ${
        awaitingDriver
          ? "ring-2 ring-orange-400 border-orange-300 bg-orange-50/40"
          : ""
      }`}
      data-testid={`delivery-order-${order.order_no}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          {order.code ? (
            <div
              className="text-xl font-mono font-bold text-slate-800"
              title="Κωδικός Παραγγελίας (barcode 7777XXXX)"
              data-testid={`order-code-${order.order_no}`}
            >
              #{String(order.code).padStart(4, "0")}
            </div>
          ) : (
            <div className="text-xl font-mono font-bold text-slate-800">#{order.order_no}</div>
          )}
          {order.code ? (
            <span
              className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 border border-slate-300 text-slate-600"
              title="Αριθμός Παραγγελίας (κοινός counter)"
            >
              Αρ.Παρ. {order.order_no}
            </span>
          ) : null}
          <span className={`text-[10px] uppercase px-2 py-0.5 rounded border font-bold ${meta.color}`}>
            {meta.label}
          </span>
          {awaitingDriver && (
            <span
              className="inline-flex items-center gap-1 text-[10px] uppercase px-2 py-0.5 rounded border font-bold bg-orange-100 border-orange-400 text-orange-800"
              title="Έχει εκδοθεί απόδειξη — αναμονή ανάθεσης οδηγού"
              data-testid={`awaiting-driver-badge-${order.order_no}`}
            >
              <Receipt size={11} /> Αναμένει οδηγό
            </span>
          )}
        </div>
        <div className="text-base font-mono font-bold text-slate-700">{timeAgo(order.created_at)}</div>
      </div>

      <div className="space-y-1 text-sm mb-2">
        <div className="flex items-start gap-1.5">
          <UserIcon size={13} className="mt-0.5 text-slate-400 shrink-0" />
          <div className="font-bold truncate">{order.customer_name || "—"}</div>
        </div>
        {order.phone && (
          <div className="flex items-center gap-1.5 text-slate-600">
            <Phone size={13} className="text-slate-400 shrink-0" />
            <a href={`tel:${order.phone}`} className="font-mono hover:text-blue-700">{order.phone}</a>
          </div>
        )}
        {order.address && (
          <div className="flex items-start gap-1.5 text-slate-600">
            <MapPin size={13} className="mt-0.5 text-slate-400 shrink-0" />
            <div className="line-clamp-2">{order.address}</div>
          </div>
        )}
        {order.notes && (
          <div className="flex items-start gap-1.5 text-amber-700">
            <StickyNote size={13} className="mt-0.5 shrink-0" />
            <div className="line-clamp-2 italic text-xs">{order.notes}</div>
          </div>
        )}
      </div>

      <div className="border-t border-slate-200 pt-2 flex items-center justify-between mb-3">
        <div className="text-xs text-slate-500">
          {(order.items || []).length} είδη · {itemsTotal.toFixed(2).replace(/\.00$/, "")} τμχ
        </div>
        <div className="text-lg font-bold font-mono text-emerald-700">{fmtEUR(order.total || 0)}</div>
      </div>

      {order.driver_name && (
        <div className="text-xs mb-2 px-2 py-1 rounded bg-blue-50 border border-blue-200 text-blue-800 flex items-center gap-1.5">
          <Truck size={12} /> Οδηγός: <span className="font-bold">{order.driver_name}</span>
        </div>
      )}

      {isActive && (
        <div className="grid grid-cols-2 gap-1.5">
          {/* Top-left: Ακύρωση */}
          {order.status === "NEW" ? (
            <Button
              size="sm"
              variant="outline"
              onClick={onCancel}
              className="h-9 text-red-600 hover:bg-red-50 border-red-200"
              data-testid={`order-${order.order_no}-cancel`}
            >
              <XCircle size={13} className="mr-1" /> Ακύρωση
            </Button>
          ) : (
            <span />
          )}
          {/* Top-right: Διόρθωση */}
          {order.status === "NEW" ? (
            <Button
              size="sm"
              variant="outline"
              onClick={onEdit}
              className="h-9"
              data-testid={`order-${order.order_no}-edit`}
            >
              <Pencil size={13} className="mr-1" /> Διόρθωση
            </Button>
          ) : (
            <span />
          )}
          {/* Bottom-left: Προεπισκόπηση */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onPreview?.(order)}
            className="h-9 border-2 border-slate-300 font-bold"
            data-testid={`preview-btn-${order.order_no}`}
          >
            <Eye size={13} className="mr-1" /> Προεπισκόπηση
          </Button>
          {/* Bottom-right: Αποστολή */}
          <Button
            size="sm"
            onClick={onPay}
            className="h-9 bg-emerald-600 hover:bg-emerald-700"
            data-testid={`order-${order.order_no}-pay`}
          >
            <Check size={13} className="mr-1" /> Αποστολή
          </Button>
        </div>
      )}

      {order.status === "DELIVERED" && order.delivered_at && (
        <div className="text-xs text-emerald-700 mt-1">
          ✓ Παραδόθηκε {timeAgo(order.delivered_at)}
        </div>
      )}

      {isClosed && (
        <div className="mt-2 pt-2 border-t border-slate-200 flex justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPreview?.(order)}
            className="h-10 px-3 text-sm font-bold border-2 border-slate-300"
            data-testid={`preview-btn-${order.order_no}`}
          >
            <Eye size={16} className="mr-1.5" />
            Προεπισκόπηση
          </Button>
          <DeliveryActionsMenu
            order={order}
            onPreview={onPreview}
            onReprint={onReprint}
            onReceipt={onReceipt}
            onRepeat={onRepeat}
          />
        </div>
      )}
    </Card>
  );
}

// ---------------- Sortable table view ----------------
function SortHeader({ label, sortKey, sort, onToggleSort, align = "left", className = "" }) {
  const active = sort.key === sortKey;
  const Arrow = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      onClick={() => onToggleSort(sortKey)}
      className={`px-3 py-2 text-[11px] font-bold uppercase tracking-wider select-none cursor-pointer hover:bg-slate-200 transition-colors ${
        align === "right" ? "text-right" : "text-left"
      } ${active ? "text-slate-900 bg-slate-200" : "text-slate-600"} ${className}`}
      data-testid={`delivery-table-th-${sortKey}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        {label}
        <Arrow size={11} className={active ? "opacity-100" : "opacity-40"} />
      </span>
    </th>
  );
}

function OrderTable({ orders, sort, onToggleSort, onEdit, onCancel, onDispatch, onPay, onPreview, onReprint, onReceipt, onRepeat }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 border-b-2 border-slate-200">
            <tr>
              <SortHeader label="#" sortKey="order_no" sort={sort} onToggleSort={onToggleSort} className="w-[80px]" />
              <SortHeader label="Κατάσταση" sortKey="status" sort={sort} onToggleSort={onToggleSort} />
              <SortHeader label="Δημ/γία" sortKey="created_at" sort={sort} onToggleSort={onToggleSort} />
              <SortHeader label="Πελάτης" sortKey="customer_name" sort={sort} onToggleSort={onToggleSort} />
              <SortHeader label="Τηλέφωνο" sortKey="phone" sort={sort} onToggleSort={onToggleSort} />
              <SortHeader label="Διεύθυνση" sortKey="address" sort={sort} onToggleSort={onToggleSort} />
              <SortHeader label="Τμχ" sortKey="items" sort={sort} onToggleSort={onToggleSort} align="right" />
              <SortHeader label="Σύνολο" sortKey="total" sort={sort} onToggleSort={onToggleSort} align="right" />
              <SortHeader label="Οδηγός" sortKey="driver_name" sort={sort} onToggleSort={onToggleSort} />
              <th scope="col" className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-600 text-right">
                Ενέργειες
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {orders.map((o) => {
              const meta = STATUS_META[o.status] || STATUS_META.NEW;
              const isActive = o.status === "NEW" || o.status === "DISPATCHED";
              const itemsTotal = (o.items || []).reduce((s, it) => s + (Number(it.qty) || 1), 0);
              const awaitingDriver = isActive && !!o.receipt_id && !o.driver_id;
              return (
                <tr
                  key={o.id}
                  className={`hover:bg-slate-50 transition-colors ${
                    awaitingDriver ? "bg-orange-50/60" : ""
                  }`}
                  data-testid={`delivery-row-${o.order_no}`}
                >
                  <td className="px-3 py-2 font-mono font-bold text-slate-800">
                    {o.code ? (
                      <>
                        <div data-testid={`row-code-${o.order_no}`}>#{String(o.code).padStart(4, "0")}</div>
                        <div className="text-[10px] text-slate-500 font-normal">Αρ.Παρ. {o.order_no}</div>
                      </>
                    ) : (
                      <div>#{o.order_no}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1 items-start">
                      <span className={`text-[10px] uppercase px-2 py-0.5 rounded border font-bold ${meta.color}`}>
                        {meta.label}
                      </span>
                      {awaitingDriver && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase px-1.5 py-0.5 rounded border font-bold bg-orange-100 border-orange-400 text-orange-800"
                          title="Έχει εκδοθεί απόδειξη — αναμονή ανάθεσης οδηγού"
                          data-testid={`row-awaiting-driver-${o.order_no}`}
                        >
                          <Receipt size={10} /> Αναμένει οδηγό
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-base text-slate-700 font-mono font-bold whitespace-nowrap">
                    {timeAgo(o.created_at)}
                  </td>
                  <td className="px-3 py-2 font-bold text-slate-800 max-w-[200px] truncate" title={o.customer_name}>
                    {o.customer_name || "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-slate-600 whitespace-nowrap">
                    {o.phone ? (
                      <a href={`tel:${o.phone}`} className="hover:text-blue-700">{o.phone}</a>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-slate-600 max-w-[260px] truncate" title={o.address}>
                    {o.address || "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                    {itemsTotal.toFixed(2).replace(/\.00$/, "")}
                  </td>
                  <td className="px-3 py-2 text-right font-bold font-mono text-emerald-700 whitespace-nowrap">
                    {fmtEUR(o.total || 0)}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700">
                    {o.driver_name ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-800">
                        <Truck size={11} /> {o.driver_name}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {isActive ? (
                      <div className="inline-flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onPreview?.(o)}
                          className="h-8 px-2"
                          data-testid={`row-${o.order_no}-preview`}
                          title="Προεπισκόπηση"
                        >
                          <Eye size={12} />
                        </Button>
                        {o.status === "NEW" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onEdit(o)}
                            className="h-8 px-2"
                            data-testid={`row-${o.order_no}-edit`}
                            title="Διόρθωση"
                          >
                            <Pencil size={12} />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => onPay(o)}
                          className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`row-${o.order_no}-pay`}
                          title="Αποστολή"
                        >
                          <Check size={12} />
                        </Button>
                        {o.status === "NEW" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onCancel(o)}
                            className="h-8 px-2 text-red-600 hover:bg-red-50"
                            data-testid={`row-${o.order_no}-cancel`}
                            title="Ακύρωση"
                          >
                            <XCircle size={12} />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onPreview?.(o)}
                          className="h-8 px-2"
                          data-testid={`row-${o.order_no}-preview`}
                          title="Προεπισκόπηση"
                        >
                          <Eye size={12} />
                        </Button>
                        <DeliveryActionsMenu
                          order={o}
                          onPreview={onPreview}
                          onReprint={onReprint}
                          onReceipt={onReceipt}
                          onRepeat={onRepeat}
                          size="sm"
                        />
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrderFormDialog({ order, drivers, onClose, onSaved }) {
  const isEdit = !!order;
  const [customer, setCustomer] = useState({
    customer_name: order?.customer_name || "",
    phone: order?.phone || "",
    address: order?.address || "",
    notes: order?.notes || "",
  });
  // Phase B: linked CRM customer (full doc when picked from CRM). When
  // set, the order is attributed to this customer card and the cashier
  // sees the «Πελάτης» chip in the form header.
  const [linkedCustomer, setLinkedCustomer] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Phase B+ — top items dialog for the linked customer
  const [topItemsCustomer, setTopItemsCustomer] = useState(null);
  const [items, setItems] = useState(order?.items || []);
  const [driverId, setDriverId] = useState(order?.driver_id || "");
  const [dispatch, setDispatch] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, c] = await Promise.all([http.get("/products?channel=delivery"), http.get("/categories")]);
        if (cancelled) return;
        setProducts((p.data || []).filter((x) => x.is_active && !x.sale_paused));
        setCategories(c.data || []);
      } catch {
        /* noop */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const total = useMemo(
    () => items.reduce((s, it) => s + Number(it.price || 0) * Number(it.qty || 1), 0),
    [items],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (p.is_hidden) return false;
      if (activeCat !== "all" && p.category_id !== activeCat) return false;
      if (q) {
        const hay = `${p.description} ${p.code} ${p.barcode1 || ""} ${p.barcode2 || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [products, activeCat, search]);

  const addItem = (p) => {
    warnZeroStock(p);
    setItems((prev) => {
      // Dedup by product id when available (catalog tap), otherwise by
      // (description + price) — top-items quick-add comes without an id.
      const key = p.id || `${p.description}|${p.price}`;
      const idx = prev.findIndex((x) => (x._pid || `${x.description}|${x.price}`) === key);
      if (idx >= 0) {
        const cp = [...prev];
        cp[idx] = { ...cp[idx], qty: Number(cp[idx].qty || 1) + 1 };
        return cp;
      }
      return [
        ...prev,
        {
          _pid: p.id || key,
          description: p.description,
          qty: 1,
          price: Number(p.price || 0),
          vat_rate: p.vat_rate,
          department: p.department,
        },
      ];
    });
  };

  const setQty = (idx, qty) => {
    setItems((prev) => {
      const cp = [...prev];
      if (qty <= 0) {
        cp.splice(idx, 1);
        return cp;
      }
      cp[idx] = { ...cp[idx], qty };
      return cp;
    });
  };

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    if (items.length === 0) {
      toast.error("Πρόσθεσε τουλάχιστον ένα είδος");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...customer,
        items: items.map(({ _pid, ...rest }) => rest), // strip _pid
        driver_id: driverId || null,
        customer_id: linkedCustomer?.id || order?.customer_id || null,
      };
      if (isEdit) {
        const r = await http.put(`/delivery/orders/${order.id}`, payload);
        // Feb 2026: εμφάνιση toast αν τυπώθηκαν διορθώσεις/ακυρώσεις
        // στις κουζίνες. Επίσης badge από τους εκτυπωτές που τους έλαβαν.
        const mods = Number(r.headers?.["x-correction-modifications"] || 0);
        const cancels = Number(r.headers?.["x-correction-cancellations"] || 0);
        const added = Number(r.headers?.["x-correction-added"] || 0);
        const printed = Number(r.headers?.["x-printed-tickets"] || 0);
        const printers = decodeURIComponent(r.headers?.["x-printed-printers"] || "")
          .split(";").filter(Boolean);
        toast.success("Αποθηκεύτηκε");
        if (printed > 0 || added > 0) {
          const parts = [];
          if (cancels > 0) parts.push(`${cancels} ακύρωσ${cancels === 1 ? "η" : "εις"}`);
          if (mods > 0) parts.push(`${mods} διόρθωσ${mods === 1 ? "η" : "εις"}`);
          if (added > 0) parts.push(`${added} νέο είδος${added === 1 ? "" : "α"}`);
          toast.info(
            `Χαρτάκια κουζίνας: ${parts.join(" + ")}` +
              (printers.length ? ` → ${[...new Set(printers)].join(", ")}` : ""),
            { duration: 6000 },
          );
        }
      } else {
        await http.post("/delivery/orders", { ...payload, dispatch: dispatch && !!driverId });
        toast.success("Καταχωρήθηκε");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        hideClose
        className="max-w-6xl w-[96vw] h-[92vh] p-0 overflow-hidden flex flex-col gap-0"
      >
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Truck size={20} /> {isEdit ? `Διόρθωση #${order.code ? String(order.code).padStart(4, "0") : order.order_no}` : "Νέα Παραγγελία Διανομής"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Στοιχεία πελάτη και επιλογή ειδών από τον κατάλογο.
          </DialogDescription>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="order-form-close">
            <X size={16} />
          </Button>
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[380px_1fr] min-h-0">
          {/* Left: customer + cart */}
          <div className="border-r border-slate-200 flex flex-col min-h-0">
            <div className="p-4 space-y-2 border-b border-slate-200">
              {/* CRM customer chip (Phase B) — pick existing card or create new */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  data-testid="delivery-customer-picker-btn"
                  className={`flex-1 inline-flex items-center gap-2 px-3 h-10 rounded-md border-2 text-sm font-bold transition-all ${
                    linkedCustomer
                      ? "bg-blue-50 border-blue-400 text-blue-800 hover:bg-blue-100"
                      : "bg-slate-100 border-slate-300 text-slate-600 hover:bg-slate-200 border-dashed"
                  }`}
                  title={linkedCustomer ? "Αλλαγή πελάτη" : "Επιλογή πελάτη"}
                >
                  <UserIcon size={14} className="shrink-0" />
                  <span className="truncate" data-testid="delivery-customer-label">
                    {linkedCustomer
                      ? `${linkedCustomer.name}${linkedCustomer.code ? ` · #${linkedCustomer.code}` : ""}`
                      : "Επιλογή πελάτη..."}
                  </span>
                  {linkedCustomer && (
                    <span
                      role="button"
                      tabIndex={-1}
                      onClick={(e) => {
                        e.stopPropagation();
                        setLinkedCustomer(null);
                      }}
                      data-testid="delivery-customer-clear"
                      className="ml-auto hover:bg-blue-200 rounded-full p-0.5"
                    >
                      <X size={12} />
                    </span>
                  )}
                </button>
              </div>
              <div>
                <Label className="text-xs">Όνομα Πελάτη</Label>
                <Input
                  value={customer.customer_name}
                  onChange={(e) => setCustomer({ ...customer, customer_name: e.target.value })}
                  className="h-10"
                  data-testid="cust-name"
                />
              </div>
              <div>
                <Label className="text-xs">Τηλέφωνο</Label>
                <Input
                  value={customer.phone}
                  inputMode="tel"
                  onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                  className="h-10 font-mono"
                  data-testid="cust-phone"
                />
              </div>
              <div>
                <Label className="text-xs">Διεύθυνση</Label>
                <Input
                  value={customer.address}
                  onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
                  className="h-10"
                  data-testid="cust-address"
                />
              </div>
              <div>
                <Label className="text-xs">Σημείωση</Label>
                <Input
                  value={customer.notes}
                  onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
                  className="h-10"
                  placeholder="π.χ. 2ος όροφος, κουδούνι Παπαδ."
                  data-testid="cust-notes"
                />
              </div>
              {!isEdit && (
                <div>
                  <Label className="text-xs">Οδηγός (προαιρετικά)</Label>
                  <div className="flex gap-2">
                    <select
                      value={driverId}
                      onChange={(e) => setDriverId(e.target.value)}
                      className="h-10 flex-1 px-2 border border-slate-300 rounded-md bg-white text-sm"
                      data-testid="cust-driver"
                    >
                      <option value="">— χωρίς οδηγό —</option>
                      {drivers.filter((d) => d.active).map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap">
                      <Switch
                        checked={dispatch}
                        onCheckedChange={setDispatch}
                        disabled={!driverId}
                        data-testid="cust-dispatch"
                      />
                      Άμεση
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto scroll-thin p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">
                Είδη ({items.length})
              </div>
              {items.length === 0 ? (
                <div className="text-center text-slate-400 text-sm py-6">— καμία γραμμή —</div>
              ) : (
                <div className="space-y-1.5">
                  {items.map((it, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 bg-slate-50 rounded-md px-2 py-1.5 text-sm"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{it.description}</div>
                        <div className="text-[11px] text-slate-500 font-mono">
                          {fmtEUR(it.price)} × {it.qty} = {fmtEUR(it.price * it.qty)}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setQty(idx, Number(it.qty) - 1)}
                          className="w-7 h-7 rounded-md border border-slate-300 bg-white hover:bg-slate-100 text-lg font-bold"
                          data-testid={`item-${idx}-minus`}
                        >
                          −
                        </button>
                        <div className="w-8 text-center font-bold font-mono">{it.qty}</div>
                        <button
                          type="button"
                          onClick={() => setQty(idx, Number(it.qty) + 1)}
                          className="w-7 h-7 rounded-md border border-slate-300 bg-white hover:bg-slate-100 text-lg font-bold"
                          data-testid={`item-${idx}-plus`}
                        >
                          +
                        </button>
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          className="w-7 h-7 ml-1 rounded-md border border-red-200 bg-white hover:bg-red-50 text-red-600 grid place-items-center"
                          data-testid={`item-${idx}-del`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t-2 border-slate-300 p-4 bg-slate-50">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                  Σύνολο
                </div>
                <div className="text-3xl font-bold font-mono text-emerald-700" data-testid="form-total">
                  {fmtEUR(total)}
                </div>
              </div>
              <Button
                onClick={save}
                disabled={busy || items.length === 0}
                className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700"
                data-testid="order-form-save"
              >
                <Check size={16} className="mr-1.5" />
                {busy ? "Αποθήκευση..." : isEdit ? "Αποθήκευση" : "Καταχώρηση"}
              </Button>
            </div>
          </div>

          {/* Right: product picker */}
          <div className="flex flex-col min-h-0 bg-white">
            <div className="p-3 border-b border-slate-200 flex items-center gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Αναζήτηση είδους..."
                  className="h-10 pl-8"
                  data-testid="product-search"
                />
              </div>
            </div>

            <div className="px-3 py-2 border-b border-slate-200 flex gap-1.5 flex-wrap">
              <CatChip active={activeCat === "all"} onClick={() => setActiveCat("all")} color="#3b82f6">
                Όλα
              </CatChip>
              {categories.map((c) => (
                <CatChip
                  key={c.id}
                  active={activeCat === c.id}
                  onClick={() => setActiveCat(c.id)}
                  color={c.color || "#94a3b8"}
                >
                  {c.name}
                </CatChip>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto scroll-thin p-3">
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                {filtered.map((p) => {
                  const cat = categories.find((c) => c.id === p.category_id);
                  const bg = p.color || cat?.color || "#1e293b";
                  const sb = stockBadge(p, 5);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => addItem(p)}
                      data-testid={`prod-${p.code || p.id}`}
                      className="relative rounded-md p-2 text-left transition-all hover:brightness-110 active:scale-[0.97] shadow-sm border border-white/20"
                      style={{ backgroundColor: bg, color: contrastText(bg) }}
                    >
                      <div className="text-xs font-bold line-clamp-2 min-h-[2.5rem]">{p.description}</div>
                      <div className="mt-1 inline-block bg-white/95 text-slate-900 rounded px-1.5 py-0.5 text-sm font-mono font-bold">
                        {fmtEUR(p.price)}
                      </div>
                      {sb && (
                        <div className={`absolute bottom-1 right-1 min-w-[22px] px-1 py-0.5 rounded text-[10px] font-mono font-bold text-center shadow ${sb.cls}`}>
                          {sb.qty}
                        </div>
                      )}
                    </button>
                  );
                })}
                {filtered.length === 0 && (
                  <div className="col-span-full text-center text-slate-400 py-12 text-sm">
                    Δεν βρέθηκαν είδη
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Customer picker mount inside form dialog (Phase B CRM) */}
        <CustomerPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          showNewButton={true}
          onSelect={(c) => {
            setLinkedCustomer(c);
            const def = (c.addresses || []).find((a) => a.is_default && a.status === "active")
              || (c.addresses || []).find((a) => a.status === "active");
            const phoneStr = (c.phones || [])[0] || "";
            // Compose a friendly address string. Street now lives on the
            // address; fall back to legacy customer.street for older docs.
            const streetParts = def
              ? [def.street, def.street_no].filter(Boolean).join(" ")
              : [c.street, c.street_no].filter(Boolean).join(" ");
            const areaParts = def ? [def.area, def.floor && `όρ. ${def.floor}`, def.doorbell && `κορδ. ${def.doorbell}`].filter(Boolean).join(", ") : "";
            const composed = [streetParts, areaParts].filter(Boolean).join(" — ");
            setCustomer({
              customer_name: c.name || "",
              phone: phoneStr,
              address: composed,
              notes: def?.instructions || c.note || "",
            });
            toast.success(`Επιλέχθηκε: ${c.name}`);
          }}
          onSelectWithRepeat={({ customer: c, lastOrder }) => {
            // Same auto-fill as plain select…
            setLinkedCustomer(c);
            const def = (c.addresses || []).find((a) => a.is_default && a.status === "active")
              || (c.addresses || []).find((a) => a.status === "active");
            const phoneStr = (c.phones || [])[0] || "";
            const streetParts = def
              ? [def.street, def.street_no].filter(Boolean).join(" ")
              : [c.street, c.street_no].filter(Boolean).join(" ");
            const areaParts = def ? [def.area, def.floor && `όρ. ${def.floor}`, def.doorbell && `κορδ. ${def.doorbell}`].filter(Boolean).join(", ") : "";
            const composed = [streetParts, areaParts].filter(Boolean).join(" — ");
            setCustomer({
              customer_name: c.name || "",
              phone: phoneStr,
              address: composed,
              notes: def?.instructions || c.note || "",
            });
            // …plus replace cart items with the last receipt.
            const repItems = (lastOrder.items || []).map((it, i) => ({
              _pid: `repeat-${i}`,
              description: it.description,
              qty: Number(it.qty || 1),
              price: Number(it.price || 0),
              vat_rate: it.vat_rate,
              department: it.department,
            }));
            setItems(repItems);
            toast.success(
              `Επανάληψη τελευταίας: ${c.name} · ${repItems.length} είδ. · ${Number(lastOrder.total_amount || 0).toFixed(2)}€`,
            );
          }}
          onSelectWithTopItems={(c) => {
            // Same auto-fill, then pop the top-items dialog so the
            // cashier can suggest favourites one tap at a time.
            setLinkedCustomer(c);
            const def = (c.addresses || []).find((a) => a.is_default && a.status === "active")
              || (c.addresses || []).find((a) => a.status === "active");
            const phoneStr = (c.phones || [])[0] || "";
            const streetParts = def
              ? [def.street, def.street_no].filter(Boolean).join(" ")
              : [c.street, c.street_no].filter(Boolean).join(" ");
            const areaParts = def ? [def.area, def.floor && `όρ. ${def.floor}`, def.doorbell && `κορδ. ${def.doorbell}`].filter(Boolean).join(", ") : "";
            const composed = [streetParts, areaParts].filter(Boolean).join(" — ");
            setCustomer({
              customer_name: c.name || "",
              phone: phoneStr,
              address: composed,
              notes: def?.instructions || c.note || "",
            });
            setTopItemsCustomer(c);
          }}
        />
        {topItemsCustomer && (
          <CustomerTopItemsDialog
            customer={topItemsCustomer}
            onOpenChange={(o) => !o && setTopItemsCustomer(null)}
            onAddItem={(it) => addItem(it)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CatChip({ active, onClick, children, color }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 h-9 rounded-md text-xs font-bold border-2 transition-all ${
        active
          ? "text-white shadow-md"
          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
      }`}
      style={active ? { backgroundColor: color, borderColor: color } : {}}
    >
      {children}
    </button>
  );
}

function contrastText(hex) {
  try {
    const c = hex.startsWith("#") ? hex.slice(1) : hex;
    const r = parseInt(c.slice(0, 2), 16);
    const g = parseInt(c.slice(2, 4), 16);
    const b = parseInt(c.slice(4, 6), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.6 ? "#1e293b" : "#ffffff";
  } catch {
    return "#ffffff";
  }
}

// ---------------- Driver assignment dialog ----------------
function DispatchDialog({ order, drivers, onClose, onSaved }) {
  const [driverId, setDriverId] = useState(order?.driver_id || "");
  const [busy, setBusy] = useState(false);
  const onSubmit = async () => {
    if (!driverId) {
      toast.error("Επιλέξτε οδηγό");
      return;
    }
    setBusy(true);
    try {
      await http.patch(`/delivery/orders/${order.id}/dispatch`, { driver_id: driverId });
      toast.success("Ανατέθηκε");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Ανάθεση Οδηγού — #{order?.code ? String(order.code).padStart(4, "0") : order?.order_no}</DialogTitle>
          <DialogDescription>Επιλέξτε διαθέσιμο οδηγό για παράδοση.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {drivers.filter((d) => d.active).length === 0 && (
            <div className="text-sm text-slate-500 text-center py-6">
              Δεν υπάρχουν ενεργοί οδηγοί. Δημιουργήστε από το κουμπί «Οδηγοί».
            </div>
          )}
          {drivers
            .filter((d) => d.active)
            .map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setDriverId(d.id)}
                className={`w-full text-left rounded-md p-3 border-2 transition-all ${
                  driverId === d.id
                    ? "border-blue-600 bg-blue-50"
                    : "border-slate-200 bg-white hover:border-slate-400"
                }`}
                data-testid={`dispatch-driver-${d.id}`}
              >
                <div className="font-bold">{d.name}</div>
                {d.phone && <div className="text-xs text-slate-500 font-mono">{d.phone}</div>}
              </button>
            ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Άκυρο</Button>
          <Button
            onClick={onSubmit}
            disabled={busy || !driverId}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="dispatch-confirm"
          >
            <PlayCircle size={14} className="mr-1.5" /> Ανάθεση
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Drivers CRUD dialog ----------------
function DriverMgmtDialog({ drivers, isAdmin, onClose, onChanged }) {
  const [form, setForm] = useState({ name: "", phone: "", active: true });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const startEdit = (d) => {
    setEditing(d);
    setForm({ name: d.name, phone: d.phone || "", active: d.active });
  };
  const cancelEdit = () => {
    setEditing(null);
    setForm({ name: "", phone: "", active: true });
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Όνομα οδηγού");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await http.put(`/drivers/${editing.id}`, form);
        toast.success("Αποθηκεύτηκε");
      } else {
        await http.post("/drivers", form);
        toast.success("Δημιουργήθηκε");
      }
      cancelEdit();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    } finally {
      setBusy(false);
    }
  };

  const del = async (d) => {
    if (!(await confirmDialog(`Διαγραφή οδηγού «${d.name}»;`))) return;
    try {
      await http.delete(`/drivers/${d.id}`);
      toast.success("Διαγράφηκε");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία");
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UsersIcon size={18} /> Οδηγοί Διανομής</DialogTitle>
          <DialogDescription>Διαχείριση οδηγών (μόνο διαχειριστές).</DialogDescription>
        </DialogHeader>

        <div className="max-h-[40vh] overflow-y-auto space-y-1.5 mb-3">
          {drivers.length === 0 && (
            <div className="text-center text-slate-400 py-4 text-sm">Κανένας οδηγός</div>
          )}
          {drivers.map((d) => (
            <div
              key={d.id}
              className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 bg-white"
              data-testid={`driver-row-${d.id}`}
            >
              <div className={`w-2 h-2 rounded-full ${d.active ? "bg-emerald-500" : "bg-slate-300"}`} />
              <div className="flex-1">
                <div className="font-bold">{d.name}</div>
                {d.phone && <div className="text-xs text-slate-500 font-mono">{d.phone}</div>}
              </div>
              {isAdmin && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => startEdit(d)} data-testid={`driver-edit-${d.id}`}>
                    <Pencil size={13} />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => del(d)} className="text-red-600" data-testid={`driver-del-${d.id}`}>
                    <Trash2 size={13} />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>

        {isAdmin && (
          <div className="border-t border-slate-200 pt-3 space-y-2">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">
              {editing ? `Επεξεργασία: ${editing.name}` : "Νέος Οδηγός"}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Όνομα</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="h-10"
                  data-testid="driver-name"
                />
              </div>
              <div>
                <Label className="text-xs">Τηλέφωνο</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="h-10 font-mono"
                  data-testid="driver-phone"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="driver-active"
              />
              Ενεργός
            </label>
            <div className="flex gap-2 pt-1">
              <Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 h-10 flex-1" data-testid="driver-save">
                <Check size={14} className="mr-1" /> {editing ? "Αποθήκευση" : "Δημιουργία"}
              </Button>
              {editing && (
                <Button variant="outline" onClick={cancelEdit} className="h-10">
                  Άκυρο
                </Button>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Κλείσιμο</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Payment / Deliver dialog ----------------
function DeliveryPayDialog({ order, onClose, onPaid, onPrintProforma, onAssignDriver }) {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentId, setPaymentId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await http.get("/payments");
        const list = r.data || [];
        setPaymentMethods(list);
        // default cash
        const cash = list.find((p) => (p.name || "").toUpperCase().includes("ΜΕΤΡΗΤ"));
        setPaymentId(cash?.payment_id || list[0]?.payment_id || 1);
      } catch {
        setPaymentId(1);
      }
    })();
  }, []);

  const saveAndDeliver = async () => {
    // «Αποθήκευση» (δισκέτα): print proforma + move order to «Εκτελεσμένες»
    // (DELIVERED) without issuing a fiscal receipt. Feb 2026 spec:
    // ολοκληρωμένη = παραδόθηκε σε οδηγό. Απαιτούμε driver_id, αλλιώς η
    // παραγγελία παραμένει στις Ενεργές.
    setBusy(true);
    try {
      try { onPrintProforma?.(order); } catch { /* noop */ }
      if (order.driver_id) {
        await http.patch(`/delivery/orders/${order.id}/deliver`, { receipt_id: null });
        toast.success("Αποθηκεύτηκε & μεταφέρθηκε στις Εκτελεσμένες");
      } else {
        toast.info("Εκτυπώθηκε δελτίο. Ανάθεσε οδηγό για ολοκλήρωση.");
      }
      onPaid();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία αποθήκευσης");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      // 1. Issue fiscal receipt
      const items = (order.items || []).map((it) => ({
        description: it.description,
        qty: Number(it.qty || 1),
        price: Number(it.price || 0),
        vat_rate: it.vat_rate,
        department: it.department,
      }));
      // Footer lines on the customer-facing slip (NONFISCALLINE):
      //   • customer / phone / address (operational info)
      //   • driver attribution (if assigned)
      //   • «ΕΥΧΑΡΙΣΤΟΥΜΕ ΓΙΑ ΤΗΝ ΠΡΟΤΙΜΗΣΗ ΣΑΣ!» — Phase B spec: only
      //     Delivery receipts carry this thank-you line.
      const customerLines = [
        order.customer_name && `ΠΕΛΑΤΗΣ: ${order.customer_name}`,
        order.phone && `ΤΗΛ: ${order.phone}`,
        order.address && `ΔΙΕΥΘΥΝΣΗ: ${order.address}`,
        order.driver_name && `ΟΔΗΓΟΣ: ${order.driver_name}`,
        " ",
        "ΕΥΧΑΡΙΣΤΟΥΜΕ ΓΙΑ ΤΗΝ ΠΡΟΤΙΜΗΣΗ ΣΑΣ!",
      ].filter((x) => x !== undefined && x !== null && x !== false);
      const r = await http.post("/ftext/send-receipt", {
        items,
        payments: [{ payment_id: paymentId }],
        non_fiscal_lines: customerLines,
        comment: order.notes || null,
        customer_id: order.customer_id || null,
        // Ιούν 2026: το απόθεμα μετρήθηκε ήδη όταν καταχωρήθηκε η παραγγελία.
        stock_already_counted: true,
      });
      const receiptId = r.data?.log?.id || null;
      // 2. Mark delivered — ALLA (Feb 2026 spec): ολοκληρωμένη είναι
      // μόνο η παραγγελία που έχει ΟΔΗΓΟ. Αν δεν υπάρχει driver_id, η
      // απόδειξη κόπηκε αλλά η παραγγελία ΠΑΡΑΜΕΝΕΙ στις «Ενεργές» με
      // status=NEW/DISPATCHED, απλά έχει receipt_id set.
      if (order.driver_id) {
        await http.patch(`/delivery/orders/${order.id}/deliver`, { receipt_id: receiptId });
        toast.success("Παραδόθηκε & εκδόθηκε απόδειξη");
      } else {
        await http.put(`/delivery/orders/${order.id}`, { receipt_id: receiptId });
        toast.success("Εκδόθηκε απόδειξη — ανάθεσε σε οδηγό για ολοκλήρωση");
      }
      // Auto-print the proforma alongside the fiscal receipt — per
      // Feb 2026 spec the operator always wants the driver-slip in hand
      // when «Έκδοση & Παράδοση» is confirmed.
      try { onPrintProforma?.(order); } catch { /* noop */ }
      onPaid();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία είσπραξης");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Αποστολή Παραγγελίας — #{order.code ? String(order.code).padStart(4, "0") : order.order_no}</DialogTitle>
          <DialogDescription>Εκτύπωση προφόρμας · Αποθήκευση · Ανάθεση οδηγού · Έκδοση απόδειξης.</DialogDescription>
        </DialogHeader>

        <div className="bg-slate-50 rounded-md p-3 text-sm space-y-1 mb-3">
          {order.customer_name && (
            <div><span className="text-slate-500">Πελάτης:</span> <span className="font-bold">{order.customer_name}</span></div>
          )}
          {order.address && <div><span className="text-slate-500">Διεύθυνση:</span> {order.address}</div>}
          {order.driver_name && <div><span className="text-slate-500">Οδηγός:</span> <span className="font-bold">{order.driver_name}</span></div>}
        </div>

        {/* Quick actions row — proforma / save / assign driver */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => onPrintProforma?.(order)}
            className="h-14 flex-col gap-0.5 font-bold border-2 border-blue-200 hover:bg-blue-50 text-blue-700"
            data-testid="pay-dialog-print-proforma"
          >
            <span className="text-lg">🖨️</span>
            <span className="text-[11px]">Προφόρμα</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={saveAndDeliver}
            disabled={busy}
            className="h-14 flex-col gap-0.5 font-bold border-2 border-slate-200 hover:bg-slate-50 text-slate-700"
            data-testid="pay-dialog-save"
            title="Εκτύπωση προφόρμας & μεταφορά στις Εκτελεσμένες"
          >
            <span className="text-lg">💾</span>
            <span className="text-[11px]">Αποθήκευση</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => onAssignDriver?.(order)}
            className="h-14 flex-col gap-0.5 font-bold border-2 border-indigo-200 hover:bg-indigo-50 text-indigo-700"
            data-testid="pay-dialog-assign-driver"
          >
            <span className="text-lg">🚚</span>
            <span className="text-[11px]">{order.driver_name ? "Αλλαγή Οδηγού" : "Ανάθεση Οδηγού"}</span>
          </Button>
        </div>

        <div className="text-center py-3 border-y-2 border-slate-300">
          <div className="text-xs uppercase tracking-wider text-slate-500">Σύνολο προς είσπραξη</div>
          <div className="text-5xl font-bold font-mono text-emerald-700" data-testid="pay-total">
            {fmtEUR(order.total || 0)}
          </div>
        </div>

        <div className="mt-3">
          <Label className="text-xs">Τρόπος Πληρωμής</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {paymentMethods.map((pm) => {
              const isCash = (pm.name || "").toUpperCase().includes("ΜΕΤΡΗΤ");
              const Icon = isCash ? Banknote : CreditCard;
              return (
                <button
                  key={pm.payment_id}
                  type="button"
                  onClick={() => setPaymentId(pm.payment_id)}
                  data-testid={`pay-method-${pm.payment_id}`}
                  className={`h-12 rounded-md border-2 font-bold text-sm flex items-center justify-center gap-1.5 transition-all ${
                    paymentId === pm.payment_id
                      ? "bg-emerald-600 border-emerald-700 text-white shadow-md"
                      : "bg-white border-slate-200 hover:border-slate-400"
                  }`}
                >
                  <Icon size={16} /> {pm.name}
                </button>
              );
            })}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Άκυρο</Button>
          <Button
            onClick={submit}
            disabled={busy || !paymentId}
            className="bg-emerald-600 hover:bg-emerald-700 h-11 text-base"
            data-testid="pay-confirm"
          >
            <Check size={16} className="mr-1.5" /> {busy ? "Έκδοση..." : "Έκδοση & Παράδοση"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
